import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  Check,
  ChevronDown,
  Star,
  Tag,
  Ticket,
  MapPin,
  Plus,
  Search,
  X,
} from "lucide-react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";

import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ModeToggle } from "@/components/ModeToggle";
import { AutoT } from "@/i18n/AutoT";
import { useLanguage } from "@/i18n/LanguageContext";
import { businessGetPublicEvents, type BusinessEventPublic } from "@/lib/business-public.functions";
import { businessTrackEvent } from "@/lib/business-track.functions";
import { analyticsSessionId } from "@/lib/analytics-session";
import { cn } from "@/lib/utils";

type QuickRange = "all" | "today" | "week" | "month";

export const Route = createFileRoute("/business/events")({
  // Prime the cache on the server so the first paint already has events
  // instead of showing skeletons through a post-hydration round trip.
  loader: ({ context }) =>
    (context as any).queryClient?.ensureQueryData({
      queryKey: ["business", "public-events"],
      queryFn: () => businessGetPublicEvents(),
      staleTime: 60_000,
    }),

  head: () => {
    const url = "https://whatfinland.com/business/events";
    const description = "Find events and networking opportunities in Finland.";
    return {
      meta: [
        { title: "Business Events In Finland — what Finland" },
        { name: "description", content: description },
        { property: "og:title", content: "Business Events In Finland — what Finland" },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: BusinessEventsPage,
});

/* ------------------------------------------------------------------ helpers */

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function quickWindow(range: QuickRange): { from: Date; to: Date } | null {
  if (range === "all") return null;
  const now = new Date();
  if (range === "today") return { from: startOfDay(now), to: endOfDay(now) };
  if (range === "week") {
    const to = new Date(now);
    to.setDate(to.getDate() + 7);
    return { from: startOfDay(now), to: endOfDay(to) };
  }
  const to = new Date(now);
  to.setMonth(to.getMonth() + 1);
  return { from: startOfDay(now), to: endOfDay(to) };
}

/* ------------------------------------------------------------------- filters */

function FilterPill({
  label,
  value,
  active,
  onClear,
  icon: Icon,
  children,
}: {
  label: string;
  value?: string | null;
  active: boolean;
  onClear?: () => void;
  icon?: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <Popover>
      <div
        className={cn(
          "inline-flex shrink-0 items-center rounded-full text-[13px] font-semibold ring-1 transition-all",
          active
            ? "bg-navy text-white ring-navy shadow-[0_10px_24px_-16px_rgba(16,24,40,0.95)]"
            : "bg-white text-navy ring-navy/10 hover:ring-navy/25",
        )}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            style={{ textTransform: "none" }}
            className="inline-flex items-center gap-1.5 py-2 pl-4 pr-3"
          >
            {Icon ? <Icon className={cn("size-4", active ? "opacity-90" : "text-royal")} /> : null}
            <span className="whitespace-nowrap">{value ? value : <AutoT text={label} />}</span>
            <ChevronDown className="size-3.5 opacity-70" />
          </button>
        </PopoverTrigger>
        {active && onClear ? (
          <button
            type="button"
            onClick={onClear}
            aria-label={`Clear ${label}`}
            className="mr-2 grid size-5 place-items-center rounded-full bg-white/20 text-white"
          >
            <X className="size-3" />
          </button>
        ) : null}
      </div>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={10}
        avoidCollisions={false}
        className="z-40 max-h-[min(70vh,520px)] w-auto max-w-[92vw] overflow-y-auto overscroll-contain rounded-2xl border-navy/10 p-0 shadow-[0_24px_60px_-28px_rgba(16,24,40,0.55)]"
      >
        {children}
      </PopoverContent>

    </Popover>
  );
}

function OptionList({
  options,
  value,
  onSelect,
}: {
  options: Array<{ key: string; label: string }>;
  value?: string;
  onSelect: (key?: string) => void;
}) {
  return (
    <div className="max-h-[320px] w-56 overflow-y-auto p-1.5">
      {options.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onSelect(active ? undefined : o.key)}
            style={{ textTransform: "none" }}
            className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-[13px] text-navy hover:bg-navy/5"
          >
            <span className="truncate">{o.label}</span>
            {active ? <Check className="size-4 shrink-0 text-royal" /> : null}
          </button>
        );
      })}
      {options.length === 0 ? (
        <p className="px-3 py-4 text-center text-[12.5px] text-navy/50">
          <AutoT text="No options yet" />
        </p>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- event card */

function countdownLabel(start: Date | null): string | null {
  if (!start) return null;
  const now = new Date();
  const days = Math.round((startOfDay(start).getTime() - startOfDay(now).getTime()) / 86_400_000);
  if (days < 0) return "Happening now";
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days < 7) return `In ${days} days`;
  if (days < 14) return "In 1 week";
  if (days < 31) return `In ${Math.round(days / 7)} weeks`;
  if (days < 60) return "In 1 month";
  return `In ${Math.round(days / 30)} months`;
}

function EventCard({
  event,
  categoryLabel,
  onOpen,
}: {
  event: BusinessEventPublic;
  categoryLabel?: string | null;
  onOpen: (e: BusinessEventPublic) => void;
}) {
  const { lang } = useLanguage();
  const locale = lang === "fi" ? "fi-FI" : "en-GB";
  const title = (lang === "fi" && event.title_fi) || event.title_en;
  const description = (lang === "fi" && event.description_fi) || event.description_en;
  const start = event.starts_at ? new Date(event.starts_at) : null;
  const end = event.ends_at ? new Date(event.ends_at) : null;

  const sameDay = start && end ? startOfDay(start).getTime() === startOfDay(end).getTime() : true;
  const dateLabel = start
    ? sameDay
      ? new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(start)
      : `${new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(start)} – ${new Intl.DateTimeFormat(
          locale,
          { day: "numeric", month: "short", year: "numeric" },
        ).format(end as Date)}`
    : null;
  const timeLabel =
    start && sameDay
      ? new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(start)
      : null;

  const badge = countdownLabel(start);
  const chips = [categoryLabel, event.destination].filter(Boolean) as string[];
  const price = event.price_text?.trim() || null;

  const href = event.registration_url || event.external_url || null;
  const Wrapper: React.ElementType = href ? "a" : "div";
  const wrapperProps = href
    ? { href, target: "_blank", rel: "noopener noreferrer", onClick: () => onOpen(event) }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white ring-1 ring-navy/[0.07] shadow-[0_14px_34px_-24px_rgba(16,24,40,0.35)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_26px_54px_-24px_rgba(16,24,40,0.45)] hover:ring-navy/15"
    >
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden bg-navy/5">
        {event.image_url ? (
          <img
            src={event.image_url}
            alt={title ?? "Business event"}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 size-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
          />
        ) : (
          <div className="grid size-full place-items-center bg-gradient-to-br from-navy/10 via-navy/[0.04] to-transparent text-navy/25">
            <CalendarDays className="size-9" />
          </div>
        )}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-navy/55 via-navy/15 to-transparent"
        />

        {badge ? (
          <span
            style={{ textTransform: "none" }}
            className="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold text-navy shadow-[0_6px_16px_-6px_rgba(16,24,40,0.5)] backdrop-blur"
          >
            <AutoT text={badge} />
          </span>
        ) : null}

        {event.is_featured ? (
          <span
            style={{ textTransform: "none" }}
            className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-navy/90 px-2.5 py-1 text-[10.5px] font-bold text-white shadow-lg backdrop-blur"
          >
            <Star className="size-3 fill-current text-amber-300" />
            <AutoT text="Featured" />
          </span>
        ) : null}

        {start ? (
          <div className="absolute bottom-3 left-3 flex flex-col items-center rounded-2xl bg-white/95 px-3 py-1.5 text-navy shadow-[0_10px_24px_-10px_rgba(16,24,40,0.6)] backdrop-blur">
            <span className="font-heading text-lg font-bold leading-none">
              {new Intl.DateTimeFormat(locale, { day: "numeric" }).format(start)}
            </span>
            <span className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-royal">
              {new Intl.DateTimeFormat(locale, { month: "short" }).format(start)}
            </span>
          </div>
        ) : null}
      </div>

      {/* Body */}
      <div className="flex min-w-0 flex-1 flex-col gap-2 p-4 sm:p-5">
        {chips.length ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {chips.map((c) => (
              <span
                key={c}
                style={{ textTransform: "none" }}
                className="rounded-full bg-royal/[0.08] px-2.5 py-0.5 text-[11px] font-bold text-royal"
              >
                {c}
              </span>
            ))}
          </div>
        ) : null}

        <h3
          style={{ textTransform: "none" }}
          className="font-heading text-[16px] font-bold leading-snug text-navy line-clamp-2 sm:text-[18px]"
        >
          {title}
        </h3>

        <div className="flex flex-col gap-1.5 text-[12.5px] text-navy/70">
          {dateLabel ? (
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="size-3.5 shrink-0 text-royal" />
              <span className="truncate font-medium">
                {dateLabel}
                {timeLabel ? ` · ${timeLabel}` : ""}
              </span>
            </span>
          ) : null}
          {event.venue_name || event.destination ? (
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <MapPin className="size-3.5 shrink-0 text-royal" />
              <span className="truncate font-medium">{event.venue_name || event.destination}</span>
            </span>
          ) : null}
        </div>

        {description ? (
          <p className="line-clamp-2 text-[12.5px] leading-relaxed text-navy/55">{description}</p>
        ) : null}

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-navy/[0.06] pt-3">
          {price ? (
            <span
              style={{ textTransform: "none" }}
              className="inline-flex items-center gap-1.5 text-[13px] font-bold text-navy"
            >
              <Ticket className="size-4 text-royal" />
              {price}
            </span>
          ) : (
            <span />
          )}
          {href ? (
            <span
              style={{ textTransform: "none" }}
              className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3.5 py-1.5 text-[12px] font-bold text-white transition-colors group-hover:bg-royal"
            >
              <AutoT text="Details" />
              <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          ) : null}
        </div>
      </div>
    </Wrapper>
  );
}

/* -------------------------------------------------------------------- page */

function BusinessEventsPage() {
  const navigate = useNavigate();
  const { lang } = useLanguage();

  const eventsFn = useServerFn(businessGetPublicEvents);
  const q = useQuery({
    queryKey: ["business", "public-events"],
    queryFn: () => eventsFn(),
    staleTime: 60_000,
  });

  const trackFn = useServerFn(businessTrackEvent);
  const track = (eventId: string) => {
    void trackFn({ data: { event_type: "event_view", event_id: eventId, session_id: analyticsSessionId() } }).catch(() => {});
  };


  const [search, setSearch] = useState("");
  const [quick, setQuick] = useState<QuickRange>("all");
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [area, setArea] = useState<string | undefined>(undefined);

  const allEvents = q.data?.events ?? [];
  const categories = q.data?.categories ?? [];

  // Submit-event CTA — controlled from Business admin → Homepage.
  const copy: Record<string, any> = (q.data as any)?.copy ?? {};
  const submitEnabled = String(copy.submit_event_enabled ?? "1") !== "0";
  const submitLabel =
    ((lang === "fi" && copy.submit_event_label_fi) || copy.submit_event_label_en || "Submit event") as string;
  const submitUrl = (copy.submit_event_url as string) || "/submit-event";

  const areas = useMemo(
    () => Array.from(new Set(allEvents.map((e) => e.destination).filter(Boolean) as string[])).sort(),
    [allEvents],
  );

  const dateWindow = useMemo(() => {
    if (range?.from) {
      return { from: startOfDay(range.from), to: endOfDay(range.to ?? range.from) };
    }
    return quickWindow(quick);
  }, [range, quick]);

  const needle = search.trim().toLowerCase();

  const events = useMemo(() => {
    return allEvents.filter((e) => {
      if (categoryId && e.category_id !== categoryId) return false;
      if (area && e.destination !== area) return false;
      if (dateWindow) {
        const start = e.starts_at ? new Date(e.starts_at) : null;
        const end = e.ends_at ? new Date(e.ends_at) : start;
        if (!start) return false;
        if (start > dateWindow.to) return false;
        if ((end ?? start) < dateWindow.from) return false;
      }
      if (!needle) return true;
      return [e.title_en, e.title_fi, e.description_en, e.venue_name, e.destination]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [allEvents, categoryId, area, dateWindow, needle]);

  const dateLabel = range?.from
    ? range.to && range.to.getTime() !== range.from.getTime()
      ? `${format(range.from, "d MMM")} – ${format(range.to, "d MMM")}`
      : format(range.from, "d MMM yyyy")
    : quick !== "all"
      ? quick === "today"
        ? "Today"
        : quick === "week"
          ? "This week"
          : "This month"
      : null;

  const activeCategory = categories.find((c) => c.id === categoryId);
  const categoryLabel = activeCategory
    ? (lang === "fi" && activeCategory.name_fi) || activeCategory.name_en
    : null;

  const anyFilter = Boolean(dateLabel || categoryId || area || needle);
  const clearAll = () => {
    setSearch("");
    setQuick("all");
    setRange(undefined);
    setCategoryId(undefined);
    setArea(undefined);
  };

  return (
    <div className="pb-28">
      <ModeToggle mode="business" />

      {/* HERO BAND */}
      <div className="relative overflow-hidden bg-navy text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.35]"
          style={{
            background:
              "radial-gradient(60% 90% at 12% 0%, rgba(255,255,255,0.22) 0%, transparent 60%), radial-gradient(50% 80% at 95% 20%, rgba(255,255,255,0.14) 0%, transparent 65%)",
          }}
        />
        <div className="relative mx-auto w-full max-w-5xl px-4 pb-16 pt-5 sm:px-6 sm:pb-20 sm:pt-6">
          <button
            type="button"
            onClick={() => navigate({ to: "/business", search: {} })}
            style={{ textTransform: "none" }}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/70 transition-colors hover:text-white"
          >
            <ArrowLeft className="size-4" />
            <AutoT text="Business" />
          </button>

          <header className="mt-4 grid grid-cols-[minmax(0,1fr)_auto] items-end gap-4">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55">
                <AutoT text="What Business" />
              </p>
              <h1
                style={{ textTransform: "none" }}
                className="font-heading mt-1.5 text-[30px] font-bold leading-[1.05] sm:text-[44px]"
              >
                <AutoT text="Business events" />
              </h1>
              <p className="mt-2.5 max-w-xl text-[14px] leading-relaxed text-white/70">
                <AutoT text="Find events and networking opportunities in Finland." />
              </p>
            </div>

            <div className="hidden shrink-0 gap-2 sm:flex">
              <div className="rounded-2xl bg-white/10 px-4 py-3 text-center ring-1 ring-white/15 backdrop-blur-sm">
                <div className="font-heading text-2xl font-bold leading-none">{allEvents.length}</div>
                <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/60">
                  <AutoT text="Upcoming" />
                </div>
              </div>
              {areas.length > 0 ? (
                <div className="rounded-2xl bg-white/10 px-4 py-3 text-center ring-1 ring-white/15 backdrop-blur-sm">
                  <div className="font-heading text-2xl font-bold leading-none">{areas.length}</div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/60">
                    <AutoT text="Cities" />
                  </div>
                </div>
              ) : null}

            </div>
          </header>
        </div>
      </div>

      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6">
        {/* SEARCH + FILTERS */}
        <div className="sticky top-0 z-20 -mx-4 -mt-12 rounded-t-3xl bg-background/90 px-4 pb-3 pt-4 backdrop-blur-md sm:mx-0 sm:rounded-3xl sm:px-5 sm:pb-4 sm:shadow-[0_18px_40px_-24px_rgba(16,24,40,0.45)]">

          <div className="flex items-center gap-2.5 rounded-full border border-navy/10 bg-white px-4 py-2.5 shadow-[0_1px_3px_rgba(16,24,40,0.06)] sm:px-5 sm:py-3">
            <Search className="size-[18px] shrink-0 text-navy/50" strokeWidth={2.25} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events, venues..."
              aria-label="Search business events"
              type="search"
              enterKeyHint="search"
              className="font-body h-6 min-w-0 flex-1 bg-transparent text-[16px] tracking-[-0.01em] text-navy outline-none placeholder:text-navy/40 sm:text-[15px] [&::-webkit-search-cancel-button]:hidden"
            />

            {anyFilter ? (
              <button
                type="button"
                onClick={clearAll}
                aria-label="Clear filters"
                className="grid size-7 shrink-0 place-items-center rounded-full bg-navy/5 text-navy transition-colors hover:bg-navy hover:text-white"
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>

          <div className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {/* DATE */}
            <FilterPill
              label="Date"
              icon={CalendarDays}
              value={dateLabel}
              active={Boolean(dateLabel)}
              onClear={() => {
                setRange(undefined);
                setQuick("all");
              }}
            >
              <div className="w-[320px] max-w-[92vw] p-3">
                <div className="flex flex-wrap gap-1.5">
                  {(
                    [
                      ["today", "Today"],
                      ["week", "This week"],
                      ["month", "This month"],
                      ["all", "All upcoming"],
                    ] as Array<[QuickRange, string]>
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setRange(undefined);
                        setQuick(key);
                      }}
                      style={{ textTransform: "none" }}
                      className={cn(
                        "rounded-full px-3 py-1.5 text-[12px] font-semibold ring-1 transition-colors",
                        !range?.from && quick === key
                          ? "bg-navy text-white ring-navy"
                          : "bg-white text-navy ring-navy/15 hover:bg-navy/5",
                      )}
                    >
                      <AutoT text={label} />
                    </button>
                  ))}
                </div>
                <Calendar
                  mode="range"
                  selected={range}
                  onSelect={(r) => {
                    setRange(r);
                    if (r?.from) setQuick("all");
                  }}
                  numberOfMonths={1}
                  disabled={{ before: startOfDay(new Date()) }}
                  initialFocus
                  className={cn("pointer-events-auto p-0 pt-3")}
                />
              </div>
            </FilterPill>

            {/* EVENT TYPE */}
            <FilterPill
              label="Event type"
              icon={Tag}
              value={categoryLabel}
              active={Boolean(categoryId)}
              onClear={() => setCategoryId(undefined)}
            >
              <OptionList
                options={categories.map((c) => ({
                  key: c.id,
                  label: ((lang === "fi" && c.name_fi) || c.name_en) ?? c.key,
                }))}
                value={categoryId}
                onSelect={setCategoryId}
              />
            </FilterPill>

            {/* AREA */}
            <FilterPill label="Area" icon={MapPin} value={area} active={Boolean(area)} onClear={() => setArea(undefined)}>
              <OptionList
                options={areas.map((a) => ({ key: a, label: a }))}
                value={area}
                onSelect={setArea}
              />
            </FilterPill>

            {submitEnabled ? (
              <a
                href={submitUrl}
                target={submitUrl.startsWith("http") ? "_blank" : undefined}
                rel={submitUrl.startsWith("http") ? "noreferrer" : undefined}
                style={{ textTransform: "none" }}
                className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-[13px] font-semibold text-white shadow-[0_10px_24px_-16px_rgba(16,24,40,0.95)] transition-colors hover:bg-navy/90"
              >
                <Plus className="size-4" />
                <AutoT text={submitLabel} />
              </a>
            ) : null}

          </div>
        </div>

        {/* RESULTS */}
        <section className="mt-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p style={{ textTransform: "none" }} className="text-[14px] font-semibold text-navy">
              {events.length} <AutoT text="results" />
            </p>
            <span
              style={{ textTransform: "none" }}
              className="inline-flex items-center gap-1 text-[13.5px] font-semibold text-navy"
            >
              <span className="text-navy/60">
                <AutoT text="Sort by:" />
              </span>{" "}
              <AutoT text="Upcoming" />
              <ChevronDown className="size-4 opacity-60" />
            </span>
          </div>

          {q.isLoading ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="overflow-hidden rounded-3xl bg-white ring-1 ring-navy/[0.06]">
                  <div className="aspect-[16/10] animate-pulse bg-navy/5" />
                  <div className="space-y-2 p-4">
                    <div className="h-4 w-2/3 animate-pulse rounded bg-navy/10" />
                    <div className="h-3 w-1/2 animate-pulse rounded bg-navy/5" />
                    <div className="h-3 w-full animate-pulse rounded bg-navy/5" />
                  </div>
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="rounded-2xl bg-white py-14 text-center ring-1 ring-navy/[0.06]">
              <p style={{ textTransform: "none" }} className="font-heading text-lg text-navy">
                <AutoT text="No upcoming events match your filters" />
              </p>
              <p className="mt-2 text-sm text-navy/60">
                <AutoT text="Try another date, type or area." />
              </p>
              {anyFilter ? (
                <button
                  type="button"
                  onClick={clearAll}
                  style={{ textTransform: "none" }}
                  className="mt-4 inline-flex items-center rounded-full bg-navy px-4 py-2 text-[13px] font-semibold text-white"
                >
                  <AutoT text="Clear filters" />
                </button>
              ) : null}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {events.map((e) => (
                <EventCard
                  key={e.id}
                  event={e}
                  categoryLabel={
                    (() => {
                      const c = categories.find((x) => x.id === e.category_id);
                      return c ? ((lang === "fi" && c.name_fi) || c.name_en) ?? null : null;
                    })()
                  }
                  onOpen={(x) => track(x.id)}
                />
              ))}
            </div>
          )}
        </section>

      </div>

      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6">
        <p className="mt-8 text-center text-[12.5px] text-navy/50">
          <AutoT text="Looking for leisure events?" />{" "}
          <Link to="/happening" className="font-semibold text-navy underline-offset-2 hover:underline">
            <AutoT text="See all events" />
          </Link>
        </p>
      </div>
    </div>
  );
}
